import { z } from "zod";

// External links are opened by the user. Never accept credentials or private destinations.
export const ShoppingUrl = z
  .string()
  .url()
  .max(2048)
  .refine((value) => {
    const u = new URL(value);
    const h = u.hostname.toLowerCase();
    return (
      u.protocol === "https:" &&
      !u.username &&
      !u.password &&
      !u.port &&
      !h.includes(":") &&
      !/^[\d.]+$/.test(h) &&
      h.includes(".") &&
      !/\.(local|localhost|internal|test|invalid)$/.test(h) &&
      h !== "localhost"
    );
  }, "Use a public HTTPS link without credentials");
const title = z.string().trim().min(1).max(240);
const short = z.string().trim().min(1).max(160);
export const ShoppingMoney = z
  .object({
    amount: z.number().int().min(0).max(100_000_000),
    currency: z.string().regex(/^[A-Z]{3}$/),
  })
  .strict();
export const ShoppingProductInput = z
  .object({
    title,
    merchant: short,
    url: ShoppingUrl,
    imageUrl: ShoppingUrl.optional(),
    price: ShoppingMoney.optional(),
    description: z.string().max(500).optional(),
  })
  .strict();
export const ShoppingStatus = z.enum([
  "unknown",
  "confirmed",
  "processing",
  "shipped",
  "out_for_delivery",
  "delivered",
  "cancelled",
  "returned",
]);
export const ShoppingCarrier = z.enum(["ups", "fedex", "usps", "dhl", "canada_post", "other"]);
export const ShoppingShipment = z
  .object({
    carrier: ShoppingCarrier,
    trackingNumber: z
      .string()
      .trim()
      .min(4)
      .max(100)
      .regex(/^[A-Za-z0-9 -]+$/),
    status: ShoppingStatus.default("unknown"),
    estimatedDelivery: z.string().max(100).optional(),
  })
  .strict();
export const ShoppingOrderInput = z
  .object({
    merchant: short,
    orderNumber: short,
    status: ShoppingStatus.default("confirmed"),
    url: ShoppingUrl.optional(),
    total: ShoppingMoney.optional(),
    items: z
      .array(z.object({ title, quantity: z.number().int().min(1).max(10000).default(1) }).strict())
      .max(50)
      .default([]),
    shipments: z.array(ShoppingShipment).max(20).default([]),
    note: z.string().max(1000).optional(),
  })
  .strict();
export const ShoppingProduct = ShoppingProductInput.extend({ observedAt: z.string() });
export const ShoppingEvent = z.object({
  at: z.string(),
  status: ShoppingStatus,
  source: z.enum(["manual", "bot", "gmail"]),
  detail: z.string().max(500),
});
export const ShoppingOrder = ShoppingOrderInput.extend({
  source: z.enum(["manual", "bot", "gmail"]),
  sourceAt: z.string(),
  events: z.array(ShoppingEvent).max(200),
  gmailSourceId: z.string().optional(),
  sender: z.string().optional(),
});
export const ShoppingReceipt = z.object({
  sourceId: z.string(),
  messageId: z.string(),
  sender: z.string().max(320),
  subject: z.string().max(500),
  receivedAt: z.string(),
  result: z.enum(["imported", "review", "ignored"]),
  summary: z.string().max(500),
  orderId: z.string().optional(),
});
export const ShoppingEntry = z.object({
  id: z.string(),
  kind: z.enum(["product", "order", "receipt"]),
  createdAt: z.string(),
  updatedAt: z.string(),
  product: ShoppingProduct.optional(),
  order: ShoppingOrder.optional(),
  receipt: ShoppingReceipt.optional(),
});
export const ShoppingListInput = z.object({
  kind: z.enum(["product", "order", "receipt"]).optional(),
  q: z.string().max(120).optional(),
  cursor: z.string().max(100).optional(),
});
export const ShoppingSource = z.object({
  id: z.string(),
  serverId: z.string(),
  botId: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  lastSyncAt: z.string().nullable(),
  nextSyncAt: z.string(),
  error: z.string().nullable(),
});
export const ShoppingSourceInput = z
  .object({ serverId: z.string().min(1), botId: z.string().min(1), enabled: z.boolean() })
  .strict();
export const ShoppingSources = z.object({
  sources: z.array(ShoppingSource),
  accounts: z.array(z.object({ id: z.string(), name: z.string() })),
  bots: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type ShoppingProductInputType = z.infer<typeof ShoppingProductInput>;
export type ShoppingOrderType = z.infer<typeof ShoppingOrder>;
export type ShoppingScope = { userId: string; spaceId: string };
